import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useMemo, useState } from 'react';
import { ILease1099 } from 'src/models/dlcLease/ILeaseMaster';
import { Loader2, X } from 'src/components/common/icons';
import { ILeaseTinNameAddress, normalizeTin, normalizeTinMatchText } from './tinNameAddressMatching';

interface IProps {
    isOpen: boolean;
    lease1099: ILease1099;
    masterTin?: ILeaseTinNameAddress | null;
    isSaving: boolean;
    onClose: (discardFailedSave?: boolean) => void;
    onSave: (draft: ILease1099) => Promise<boolean>;
    onUpdate?: (destination: 'tin' | '1099') => Promise<void>;
}

type ComparisonField = 'ssn' | 'name1' | 'name2' | 'name3' | 'addr1' | 'addr2' | 'cstz';

const fields: Array<{ key: ComparisonField; label: string; masterKey: keyof ILeaseTinNameAddress }> = [
    { key: 'ssn', label: 'Tax ID', masterKey: 'tin' },
    { key: 'name1', label: 'Name 1', masterKey: 'name1' },
    { key: 'name2', label: 'Name 2', masterKey: 'name2' },
    { key: 'name3', label: 'Name 3', masterKey: 'name3' },
    { key: 'addr1', label: 'Address', masterKey: 'addr1' },
    { key: 'addr2', label: 'Address line 2', masterKey: 'addr2' },
    { key: 'cstz', label: 'City / State / ZIP', masterKey: 'cstz' },
];

const valuesMatch = (key: ComparisonField, leaseValue?: string | null, masterValue?: string | null): boolean =>
    key === 'ssn' ? normalizeTin(leaseValue) === normalizeTin(masterValue) : normalizeTinMatchText(leaseValue) === normalizeTinMatchText(masterValue);

// Use the lease's SSN/EIN presentation on both sides without changing stored values.
const formatComparisonTin = (value: string, leaseTin: string): string => {
    const normalized = normalizeTin(value);
    if (!/^\d{9}$/.test(normalized)) {
        return value;
    }
    if (/^\d{3}-\d{2}-\d{4}$/.test(leaseTin.trim())) {
        return `${normalized.slice(0, 3)}-${normalized.slice(3, 5)}-${normalized.slice(5)}`;
    }
    if (/^\d{2}-\d{7}$/.test(leaseTin.trim())) {
        return `${normalized.slice(0, 2)}-${normalized.slice(2)}`;
    }
    return normalized;
};

const TinMismatchReviewDialog: React.FC<IProps> = ({ isOpen, lease1099, masterTin, isSaving, onClose, onSave, onUpdate }) => {
    const [draft, setDraft] = useState<ILease1099>(lease1099);
    const [isDirty, setIsDirty] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false);
    const [updated, setUpdated] = useState<string | null>(null);
    const busy = isSaving || updating;
    const comparison = masterTin ? lease1099 : draft;

    const mismatchCount = useMemo(
        () => fields.filter(({ key, masterKey }) => !valuesMatch(key, comparison[key] as string | undefined, masterTin?.[masterKey])).length,
        [comparison, masterTin]
    );

    if (!isOpen) {
        return null;
    }

    const requestClose = (): void => {
        if (busy) {
            return;
        }
        if (isDirty && !window.confirm('Discard your unsaved TIN match changes?')) {
            return;
        }
        onClose(saveFailed);
    };

    const update = async (destination: 'tin' | '1099'): Promise<void> => {
        if (!onUpdate || busy) {
            return;
        }
        const message =
            destination === 'tin'
                ? 'Replace all six name/address fields in the shared TIN Master with the displayed Lease 1099 values? Blank values will clear existing fields. Other leases may reference this master; their individual 1099 records will not be changed.'
                : 'Replace all six name/address fields in this Lease 1099 with the displayed TIN Master values? Blank values will clear existing fields.';
        if (!window.confirm(message)) {
            return;
        }
        setUpdating(true);
        setUpdateError(null);
        setUpdated(null);
        try {
            await onUpdate(destination);
            setUpdated(destination === 'tin' ? 'TIN Master updated.' : 'Lease 1099 updated.');
        } catch (error) {
            setUpdateError(error instanceof Error ? error.message : 'The update failed. Refresh the comparison and try again.');
        } finally {
            setUpdating(false);
        }
    };

    const updateDraft = (key: ComparisonField, value: string): void => {
        setDraft((current) => ({ ...current, [key]: value }));
        setIsDirty(true);
    };

    const save = async (): Promise<void> => {
        const wasSaved = await onSave(draft);
        if (wasSaved) {
            setIsDirty(false);
            setSaveFailed(false);
        } else {
            setSaveFailed(true);
        }
    };

    return (
        <div style={dialogStyles.overlay} onClick={requestClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="tin-match-review-title"
                style={dialogStyles.content}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={dialogStyles.header}>
                    <div>
                        <h3 id="tin-match-review-title" style={dialogStyles.title}>
                            Review TIN Match
                        </h3>
                        <p style={dialogStyles.subtitle}>
                            {masterTin
                                ? 'Update TIN copies lease values to the shared master. Update 1099 copies master values to this lease. Both replace all six name/address fields, including blanks.'
                                : 'Correct the Lease 1099 values, then select Save Lease 1099. The TIN Master update process will run automatically.'}
                        </p>
                    </div>
                    <button type="button" onClick={requestClose} style={dialogStyles.closeButton} aria-label="Close TIN match review">
                        <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                </div>

                {updateError && (
                    <div role="alert" style={dialogStyles.missingNotice}>
                        {updateError}
                    </div>
                )}
                {updated && (
                    <div role="status" style={dialogStyles.missingNotice}>
                        {updated}
                    </div>
                )}

                {!masterTin ? (
                    <div style={dialogStyles.missingNotice}>
                        No TIN master record was found. Saving this lease 1099 record will run the existing process that creates or updates TIN master
                        data.
                    </div>
                ) : (
                    <div style={dialogStyles.summary}>
                        {mismatchCount === 0
                            ? 'The displayed values now match.'
                            : `${mismatchCount} field${mismatchCount === 1 ? ' differs' : 's differ'}.`}
                    </div>
                )}

                <div style={dialogStyles.body}>
                    <div style={dialogStyles.columnHeaders}>
                        <span />
                        <strong>{masterTin ? 'Lease 1099' : 'Lease 1099 — Editable'}</strong>
                        <strong>TIN Master</strong>
                    </div>
                    {fields.map(({ key, label, masterKey }) => {
                        const leaseValue = (comparison[key] as string | undefined) || '';
                        const masterValue = masterTin?.[masterKey] || '';
                        const matches = !!masterTin && valuesMatch(key, leaseValue, masterValue);
                        const comparisonStyle = masterTin && !matches ? dialogStyles.mismatchCell : dialogStyles.valueCell;

                        return (
                            <div key={key} style={dialogStyles.row}>
                                <label htmlFor={`tin-match-${key}`} style={dialogStyles.label}>
                                    {label}
                                </label>
                                <LeaseRecordInput
                                    id={`tin-match-${key}`}
                                    value={key === 'ssn' && masterTin ? formatComparisonTin(leaseValue, leaseValue) : leaseValue}
                                    readOnly={!!masterTin}
                                    disabled={busy}
                                    maxLength={key === 'ssn' ? 60 : 100}
                                    onChange={(e) => updateDraft(key, e.target.value)}
                                    style={{ ...dialogStyles.input, ...comparisonStyle }}
                                />
                                <div style={{ ...dialogStyles.input, ...dialogStyles.masterValue, ...comparisonStyle }} title={masterValue}>
                                    {masterTin ? (key === 'ssn' ? formatComparisonTin(masterValue, leaseValue) : masterValue) : 'Not available'}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div style={dialogStyles.footer}>
                    <button type="button" onClick={requestClose} disabled={busy} style={dialogStyles.secondaryButton}>
                        Cancel
                    </button>
                    {masterTin ? (
                        <>
                            <button
                                type="button"
                                style={dialogStyles.primaryButton}
                                disabled={busy || !onUpdate || !lease1099.lsId}
                                onClick={() => void update('tin')}
                            >
                                Update TIN
                            </button>
                            <button
                                type="button"
                                style={dialogStyles.primaryButton}
                                disabled={busy || !onUpdate || !lease1099.lsId}
                                onClick={() => void update('1099')}
                            >
                                Update 1099
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => void save()}
                            disabled={!isDirty || isSaving}
                            style={{ ...dialogStyles.primaryButton, opacity: !isDirty || isSaving ? 0.65 : 1 }}
                        >
                            {isSaving && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                            {isSaving ? 'Saving...' : 'Save Lease 1099'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const dialogStyles: Record<string, React.CSSProperties> = {
    overlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
    },
    content: {
        width: 'min(900px, 96vw)',
        maxHeight: '94vh',
        overflow: 'auto',
        borderRadius: '0.75rem',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 24px 48px rgba(15, 23, 42, 0.28)',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid #E2E8F0',
    },
    title: { margin: 0, color: '#0F172A', fontSize: '1.25rem' },
    subtitle: { margin: '0.35rem 0 0', color: '#475569', fontSize: '0.875rem' },
    closeButton: { border: 0, padding: '0.25rem', background: 'transparent', color: '#64748B', cursor: 'pointer' },
    summary: {
        margin: '1rem 1.5rem 0',
        padding: '0.75rem 1rem',
        borderRadius: '0.5rem',
        backgroundColor: '#FEF3C7',
        color: '#92400E',
        fontWeight: 600,
    },
    missingNotice: { margin: '1rem 1.5rem 0', padding: '0.75rem 1rem', borderRadius: '0.5rem', backgroundColor: '#EFF6FF', color: '#1E40AF' },
    body: { padding: '1.25rem 1.5rem' },
    columnHeaders: {
        display: 'grid',
        gridTemplateColumns: '150px minmax(0, 1fr) minmax(0, 1fr)',
        gap: '0.75rem',
        padding: '0 0.5rem 0.5rem',
        color: '#334155',
        fontSize: '0.875rem',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '150px minmax(0, 1fr) minmax(0, 1fr)',
        gap: '0.75rem',
        alignItems: 'center',
        padding: '0.5rem',
        borderTop: '1px solid #F1F5F9',
    },
    label: { color: '#334155', fontWeight: 600 },
    input: {
        width: '100%',
        minWidth: 0,
        height: '2.5rem',
        margin: 0,
        fontFamily: 'inherit',
        fontSize: 'inherit',
        lineHeight: '1.25rem',
        boxSizing: 'border-box',
        border: '1px solid #CBD5E1',
        borderRadius: '0.375rem',
        padding: '0.55rem 0.65rem',
        color: '#0F172A',
        backgroundColor: '#FFFFFF',
    },
    masterValue: { display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', overflowX: 'auto' },
    valueCell: { border: '1px solid #CBD5E1', backgroundColor: '#F8FAFC' },
    mismatchCell: { border: '1px solid #F59E0B', backgroundColor: '#FFF7ED' },
    footer: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #E2E8F0' },
    primaryButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        border: 0,
        borderRadius: '0.4rem',
        padding: '0.55rem 1rem',
        backgroundColor: '#1D4ED8',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 600,
    },
    secondaryButton: {
        border: '1px solid #CBD5E1',
        borderRadius: '0.4rem',
        padding: '0.55rem 1rem',
        backgroundColor: '#FFFFFF',
        color: '#334155',
        cursor: 'pointer',
        fontWeight: 600,
    },
};

export default TinMismatchReviewDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/TinMismatchReviewDialog.tsx